import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, throwError, from } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { AuthService } from "../services/auth.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private router: Router
  ) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    if (req.method === "OPTIONS") {
      return next.handle(req); // Pass through OPTIONS requests without modification
    }

    // Ignore requests to "api/auth" path
    if (req.url.includes("api/auth") || req.url.includes("s3.us-east-1.amazonaws.com") || req.url.includes("amazonaws.com/v2/autocomplete")) {
      return next.handle(req); // Pass through authentication-related requests without modification
    }

    const accessToken = this.authService.getAccessToken();
    const idToken = this.authService.getIdToken();

    let authReq = req;
    if (accessToken && idToken) {
      authReq = this.addToken(req, accessToken, idToken);
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Try to refresh tokens
          return from(this.authService.refreshTokens()).pipe(
            switchMap(success => {
              if (success) {
                const newAccessToken = this.authService.getAccessToken() as any;
                const newIdToken = this.authService.getIdToken() as any;

                const retryReq = this.addToken(req, newAccessToken, newIdToken);
                return next.handle(retryReq);
              } else {
                this.handleUnauthorized();
                return throwError(() => error);
              }
            }),
            catchError(() => {
              this.handleUnauthorized();
              return throwError(() => error);
            })
          );
        }

        return throwError(() => error);
      })
    );
  }

  private addToken(
    request: HttpRequest<any>,
    accessToken: string,
    idToken: string
  ) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
        "x-id-token": `${idToken}`,
      },
    });
  }

  private handleUnauthorized() {
    // Clear any stored tokens and redirect to login
    this.authService.logout(); // Clears tokens from localStorage or session
    this.router.navigate(["/login"], {
      queryParams: { returnUrl: this.router.url }, // Optionally preserve the current URL
    });
  }
}
